import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import type { EventSeriesScope } from '@/models';
import { ALL_EVENT_SERIES_SCOPES, EVENT_SERIES_SCOPE_LABELS } from '@/models';

const SCOPE_DESCRIPTIONS: Record<EventSeriesScope, string> = {
  'SINGLE':    'Nur dieser einzelne Termin wird geändert. Die Serie bleibt unberührt.',
  'FOLLOWING': 'Dieser Termin und alle späteren Termine der Serie. Vergangene Termine bleiben erhalten.',
  'ALL':       'Alle Termine der Serie, auch die vergangenen.',
};

interface EventScopeSelectorProps {
  scope: EventSeriesScope;
  onChange: (scope: EventSeriesScope) => void;
  disabled: boolean;
  colorTheme: 'blue' | 'red';
}

// Radio group for choosing which occurrences of a series an action applies to.
// Used inline in the edit modal and inside EventScopeDialog for deletions.
export function EventScopeSelector({ scope, onChange, disabled, colorTheme }: EventScopeSelectorProps) {
  const activeBorder = colorTheme === 'red'
    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
    : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';

  return (
    <div className="space-y-2">
      {ALL_EVENT_SERIES_SCOPES.map((s) => (
        <label
          key={s}
          className={`flex items-start p-3 border rounded-lg transition-colors ${
            scope === s ? activeBorder : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="radio"
            name="event-series-scope"
            value={s}
            checked={scope === s}
            onChange={() => onChange(s)}
            disabled={disabled}
            className={`mt-0.5 mr-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          />
          <span>
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              {EVENT_SERIES_SCOPE_LABELS[s]}
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {SCOPE_DESCRIPTIONS[s]}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

interface EventScopeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: (scope: EventSeriesScope) => void;
  loading: boolean;
}

export default function EventScopeDialog({ isOpen, onClose, title, description, confirmLabel, onConfirm, loading }: EventScopeDialogProps) {
  const [scope, setScope] = useState<EventSeriesScope>('SINGLE');

  const handleClose = () => {
    if (!loading) {
      setScope('SINGLE');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      icon={<FontAwesomeIcon icon={faRepeat} className="w-5 h-5 text-red-600" />}
      maxWidth="lg"
      closeDisabled={loading}
    >
      <div className="p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>

        <EventScopeSelector scope={scope} onChange={setScope} disabled={loading} colorTheme="red" />

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => onConfirm(scope)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                {confirmLabel}...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
